import { useCallback, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { cancelVideoTaskAPI, getVideoTaskStatus } from '../api'
import { useChatStore } from '../chatStore'

/**
 * 视频任务轮询 Hook
 * 负责管理视频生成任务的轮询、取消和状态更新
 */
export const useVideoTaskPolling = (conversationId: string) => {
  const { updateVideoTaskStatus, updateCurrentMessage } = useChatStore()
  const location = useLocation()

  // 视频任务轮询相关 refs
  const videoTaskPollingTimer = useRef<NodeJS.Timeout | null>(null)
  const videoTaskIdRef = useRef<string | null>(null)
  const videoTaskProviderIdRef = useRef<string | null>(null)
  // 轮询活动状态标志
  const isPollingActiveRef = useRef<boolean>(false)
  // 轮询执行锁，防止并发执行
  const isPollingExecutingRef = useRef<boolean>(false)

  /**
   * 视频任务轮询：等待上一次请求完成后再等待3秒，然后发起下一次请求
   */
  const startVideoTaskPolling = (taskId: string, chatId: string) => {
    // 先停止之前的轮询，确保不会同时运行多个轮询
    // 设置 isPollingActiveRef 为 false，这样正在执行的 pollTaskStatus 会检查到这个状态并停止
    isPollingActiveRef.current = false
    if (videoTaskPollingTimer.current) {
      clearTimeout(videoTaskPollingTimer.current)
      videoTaskPollingTimer.current = null
      // console.log('【视频任务】开始新轮询前，已清除旧的 timer')
    }

    // 设置新的轮询为活动状态
    isPollingActiveRef.current = true

    const pollTaskStatus = async () => {
      // 检查轮询是否已被停止（在发起请求之前就检查）
      if (!isPollingActiveRef.current) {
        console.log('【视频任务】轮询已被停止，不再查询')
        return
      }

      // 检查是否正在执行，防止并发执行
      if (isPollingExecutingRef.current) {
        console.log('【视频任务】轮询正在执行中，跳过本次调用')
        return
      }

      // 设置执行锁
      isPollingExecutingRef.current = true

      try {
        // 在发起请求前，先检查 store 中的消息状态，如果已经是 CANCELED，就不发起请求
        const currentState = useChatStore.getState()
        const messages = currentState.messages.filter((msg: any) => msg.role !== 'loading')
        const lastMessage = messages[messages.length - 1]
        if (
          lastMessage?.role === 'assistant' &&
          lastMessage?.content?.[0]?.generate_video?.task_status === 'CANCELED'
        ) {
          isPollingExecutingRef.current = false // 释放执行锁
          stopVideoTaskPolling()
          return
        }

        const result = await getVideoTaskStatus(taskId)

        // 再次检查轮询是否已被停止（可能在请求期间被停止）
        if (!isPollingActiveRef.current) {
          console.log('【视频任务】轮询在请求期间被停止，忽略结果，不再处理')
          isPollingExecutingRef.current = false // 释放执行锁
          return
        }

        // 再次检查消息状态，可能在请求期间状态被更新为 CANCELED
        const currentStateAfterRequest = useChatStore.getState()
        const messagesAfterRequest = currentStateAfterRequest.messages.filter((msg: any) => msg.role !== 'loading')
        const lastMessageAfterRequest = messagesAfterRequest[messagesAfterRequest.length - 1]
        if (
          lastMessageAfterRequest?.role === 'assistant' &&
          lastMessageAfterRequest?.content?.[0]?.generate_video?.task_status === 'CANCELED'
        ) {
          isPollingExecutingRef.current = false // 释放执行锁
          stopVideoTaskPolling()
          return
        }

        if (result && result.status) {
          const { status, video_url, task_id, thumbnail_url } = result

          // 更新消息状态
          if (status === 'SUCCEEDED' && video_url) {
            // 任务完成，更新消息显示视频
            updateVideoTaskStatus(chatId, {
              url: video_url,
              thumbnail_url: thumbnail_url,
              name: `视频_${task_id}`,
              is_valid: 1,
              task_status: 'SUCCEEDED',
              task_id: task_id
            })
            // 释放执行锁
            isPollingExecutingRef.current = false
            // 停止轮询
            stopVideoTaskPolling()
          } else if (status === 'FAILED') {
            // 任务失败
            updateVideoTaskStatus(chatId, {
              task_status: 'FAILED',
              url: null,
              name: null,
              is_valid: 0,
              task_id: task_id
            })
            // 释放执行锁
            isPollingExecutingRef.current = false
            stopVideoTaskPolling()
          } else if (status === 'PENDING' || status === 'RUNNING') {
            // 再次检查是否被停止（可能在更新状态时被停止）
            if (!isPollingActiveRef.current) {
              console.log('【视频任务】轮询在处理 PENDING/RUNNING 状态时被停止')
              isPollingExecutingRef.current = false // 释放执行锁
              return
            }
            // 继续轮询，更新状态
            updateVideoTaskStatus(chatId, {
              task_status: status,
              url: null,
              name: null,
              is_valid: 0,
              task_id: task_id
            })
            // 先清除旧的 timer，确保只有一个 timer 在运行
            if (videoTaskPollingTimer.current) {
              clearTimeout(videoTaskPollingTimer.current)
            }
            // 等待3秒后继续下一次轮询（从请求完成开始计时）
            videoTaskPollingTimer.current = setTimeout(() => {
              isPollingExecutingRef.current = false // 释放执行锁
              pollTaskStatus()
            }, 3000) as any
          } else if (status === 'CANCELED') {
            // 任务已取消
            updateVideoTaskStatus(chatId, {
              task_status: 'CANCELED',
              url: null,
              name: null,
              is_valid: 0,
              task_id: task_id
            })
            // 释放执行锁
            isPollingExecutingRef.current = false
            // 停止轮询（任务已取消，不需要继续轮询）
            stopVideoTaskPolling()
            return
          } else {
            // 释放执行锁
            isPollingExecutingRef.current = false
            // 其他状态，停止轮询
            stopVideoTaskPolling()
          }
        } else {
          // 如果没有返回状态，释放执行锁
          isPollingExecutingRef.current = false
        }
      } catch (error) {
        console.error('【视频任务】轮询失败:', error)
        // 如果轮询已被停止，不再更新状态
        if (!isPollingActiveRef.current) {
          console.log('【视频任务】轮询失败时发现已被停止，不更新状态')
          isPollingExecutingRef.current = false // 释放执行锁
          return
        }
        // 任务失败
        updateVideoTaskStatus(chatId, {
          task_status: 'FAILED',
          url: null,
          name: null,
          is_valid: 0,
          task_id: taskId
        })
        // 释放执行锁
        isPollingExecutingRef.current = false
        stopVideoTaskPolling()
      }
    }

    // 立即执行一次
    pollTaskStatus()
  }

  /**
   * 停止视频任务轮询
   */
  const stopVideoTaskPolling = useCallback(() => {
    // 先设置标志为 false，这样正在执行的 pollTaskStatus 会检查到这个状态
    isPollingActiveRef.current = false
    if (videoTaskPollingTimer.current) {
      clearTimeout(videoTaskPollingTimer.current)
      videoTaskPollingTimer.current = null
      console.log('【视频任务】已清除 timer')
    } else {
      console.log('【视频任务】timer 已经是 null')
    }
  }, [])

  /**
   * 取消视频任务（用于用户主动取消）
   * 先停止轮询，再调用取消接口
   */
  const cancelVideoTask = async (taskId: string) => {
    // 先停止轮询，避免取消后还会继续查询
    stopVideoTaskPolling()
    try {
      await cancelVideoTaskAPI(taskId)
    } catch (error) {
      console.error('【视频任务】取消失败:', error)
    }
  }

  /**
   * 处理视频任务输出：检测到任务后开始轮询
   */
  const handleVideoTaskOutput = (videoTaskOutput: any, appId: string) => {
    if (videoTaskOutput?.task_status === 'PENDING' && videoTaskOutput?.task_id) {
      // 先停止之前的轮询，避免多个轮询同时运行
      stopVideoTaskPolling()

      videoTaskIdRef.current = videoTaskOutput.task_id
      videoTaskProviderIdRef.current = videoTaskOutput.provider_task_id || null

      // 更新消息状态为 loading（显示视频生成中）
      updateVideoTaskStatus(appId, {
        task_status: 'PENDING',
        url: null,
        name: null,
        is_valid: 0,
        task_id: videoTaskOutput.task_id
      })

      // 开始轮询
      startVideoTaskPolling(videoTaskOutput.task_id, appId)
    }
  }

  /**
   * 查询任务状态（用于切换会话时判断是否需要取消）
   */
  const checkTaskStatusBeforeCancel = async (taskId: string): Promise<boolean> => {
    try {
      const result = await getVideoTaskStatus(taskId)
      if (result && result.status) {
        // 如果任务已经完成（SUCCEEDED、FAILED、CANCELED），不需要取消
        return ['SUCCEEDED', 'FAILED', 'CANCELED'].includes(result.status)
      }
      // 如果查询失败或状态未知，保守处理：需要取消
      return false
    } catch (error) {
      console.error('【视频任务】查询状态失败:', error)
      // 查询失败时，保守处理：需要取消
      return false
    }
  }

  /**
   * 清理视频任务：先查询状态，如果未完成才取消任务并停止轮询
   */
  const cleanupVideoTask = useCallback(async () => {
    if (videoTaskIdRef.current) {
      const taskId = videoTaskIdRef.current
      // 先停止轮询
      stopVideoTaskPolling()

      // 先查询任务状态，如果已完成就不需要取消
      const isCompleted = await checkTaskStatusBeforeCancel(taskId)
      if (!isCompleted) {
        // 任务未完成，需要取消
        try {
          await cancelVideoTaskAPI(taskId)
        } catch (err) {
          console.error('【视频任务】清理时取消失败:', err)
        }
      } else {
        console.log('【视频任务】任务已完成，无需取消:', taskId)
      }

      // 清空引用
      videoTaskIdRef.current = null
      videoTaskProviderIdRef.current = null
    }
  }, [stopVideoTaskPolling])

  /**
   * 同步查询任务状态（用于页面关闭时判断是否需要取消）
   */
  const checkTaskStatusSync = (taskId: string): boolean => {
    try {
      // 使用同步 XMLHttpRequest，确保在页面关闭时也能发送请求
      const xhr = new XMLHttpRequest()
      xhr.open('GET', `/api/v1/video/tasks/${taskId}`, false) // false 表示同步
      xhr.setRequestHeader('Content-Type', 'application/json')
      xhr.send()

      if (xhr.status === 200) {
        const result = JSON.parse(xhr.responseText)
        if (result?.data?.status) {
          // 如果任务已经完成（SUCCEEDED、FAILED、CANCELED），不需要取消
          return ['SUCCEEDED', 'FAILED', 'CANCELED'].includes(result.data.status)
        }
      }
      // 查询失败或状态未知，保守处理：需要取消
      return false
    } catch (error) {
      console.error('【视频任务】同步查询状态失败:', error)
      // 查询失败时，保守处理：需要取消
      return false
    }
  }

  /**
   * 同步取消视频任务（用于页面关闭时）
   */
  const cancelVideoTaskSync = (taskId: string) => {
    try {
      // 先查询状态，如果已完成就不需要取消
      const isCompleted = checkTaskStatusSync(taskId)
      if (isCompleted) {
        console.log('【视频任务】任务已完成，无需取消:', taskId)
        return
      }

      // 使用同步 XMLHttpRequest，确保在页面关闭时也能发送请求
      const xhr = new XMLHttpRequest()
      xhr.open('POST', `/api/v1/video/tasks/${taskId}/cancel`, false) // false 表示同步
      xhr.setRequestHeader('Content-Type', 'application/json')
      xhr.send(JSON.stringify({}))
      console.log('【视频任务】同步取消成功:', taskId)
    } catch (error) {
      console.error('【视频任务】同步取消失败:', error)
    }
  }

  // 注意：会话切换时的清理逻辑已经在 ChatInput 的 messageStoplink() 中通过 cleanupVideoTask() 处理
  // 这里不需要重复监听 conversationId，避免重复调用

  // 监听路由切换，清理视频任务
  useEffect(() => {
    // 当路由变化时（离开 /chat 页面），清理视频任务
    if (videoTaskIdRef.current) {
      cleanupVideoTask()
    }
  }, [location.pathname])

  // 监听页面关闭/路由切换，清理视频任务
  useEffect(() => {
    const historyChangeEventName = 'app-history-change'

    // 页面关闭前取消任务
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (videoTaskIdRef.current) {
        const taskId = videoTaskIdRef.current
        // 使用同步 XMLHttpRequest（页面关闭时仍能发送）
        cancelVideoTaskSync(taskId)
      }
    }

    // 监听浏览器前进/后退
    const handlePopState = () => {
      if (videoTaskIdRef.current) {
        cleanupVideoTask()
      }
    }

    const handleHistoryChange = () => {
      // 延迟执行，确保路由已经变化
      setTimeout(() => {
        if (videoTaskIdRef.current && !window.location.pathname.startsWith('/chat')) {
          cleanupVideoTask()
        }
      }, 0)
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('popstate', handlePopState)
    window.addEventListener(historyChangeEventName, handleHistoryChange)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('popstate', handlePopState)
      window.removeEventListener(historyChangeEventName, handleHistoryChange)
      // 组件卸载时清理（注意：会话切换的清理已经在 ChatInput 中处理，这里只处理组件卸载）
      if (videoTaskIdRef.current) {
        // 组件卸载时，只停止轮询，不调用完整的 cleanupVideoTask（避免重复）
        stopVideoTaskPolling()
      }
    }
  }, [stopVideoTaskPolling, cleanupVideoTask])

  return {
    videoTaskIdRef,
    videoTaskProviderIdRef,
    startVideoTaskPolling,
    stopVideoTaskPolling,
    cancelVideoTask,
    handleVideoTaskOutput,
    cleanupVideoTask,
    checkTaskStatusBeforeCancel
  }
}
